import { Modal } from '@design-system/components/modal/modal';
import { sprinkles } from '@design-system/sprinkles.css';
import { Button } from '@design-system/components/button/button';
import { clsx } from 'clsx';
import _ from 'lodash';
import { Library } from './library';
import { Typography } from '@design-system/components/typography/typography';
import { Skeleton, Tab, Tabs } from '@mui/material';
import {
  AddFromLibaryVM,
  useAddFromLibraryVM,
} from '../../view-models/addFromLibrary.viewModel';
import { ContentFilters } from '@client/new/common/library/helpers';
import { SingleUpload } from './SingleUpload';
import { IconButton } from '@design-system/components/button/iconButton';
import {
  faMagnifyingGlassMinus,
  faMagnifyingGlassPlus,
} from '@fortawesome/pro-regular-svg-icons';
import { AsyncRenderer } from '@client/new/components/AsyncRenderer/AsyncRenderer';
import { ConfirmationModal } from '@client/new/pages/creator/library/views/videoUpload/ConfirmationModal';

type AddTemplatesFromLibraryModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onAddSelection: (videos: { uuid: string; thumbnailUrl?: string }[]) => void;
  type: ContentFilters | null;
  onAddVideo: (value: {
    uuid: string;
    previewUrl?: string;
    thumbnailTimestampMs?: number;
  }) => void;
  action: 'add' | 'update';
  uploadTags?: string[];
  attachOnUpload?: boolean;
};

export const AddTemplatesFromLibraryModal = (
  p: AddTemplatesFromLibraryModalProps,
) => {
  const model = useAddFromLibraryVM({
    onClose: p.onClose,
    type: p.type,
    action: p.action,
    onAddVideo: (value) => p.onAddVideo(value),
    onAddVideoFromSelection: (v) =>
      p.onAddSelection(
        v.map((i) => ({ uuid: i.uuid, thumbnailUrl: i.thumbnailUrl })),
      ),
    isMultipleChoice: true,
    uploadTags: p.uploadTags,
    attachOnUpload: p.attachOnUpload,
  });

  return (
    <Modal
      isOpen={p.isOpen}
      onClose={p.onClose}
      title="Add from library"
      disableBackdropClick={true}
      footerClassName="p-0"
      modalClassName="w-full md:w-[800px]"
      modalContentClassName="overflow-hidden"
    >
      <AsyncRenderer
        loadRender={
          <div className={clsx('w-full flex flex-col items-center gap-2')}>
            {Array.from({ length: 5 }, (_, i) => (
              <Skeleton
                variant="rounded"
                height={50}
                key={i}
                className="w-full"
              />
            ))}
          </div>
        }
        data={model}
        renderer={(vm) => <LibraryPicker {...vm} />}
      />
    </Modal>
  );
};

export const LibraryPicker = (p: AddFromLibaryVM) => {
  return (
    <div
      className={sprinkles({
        display: 'flex',
        flexDirection: 'column',
        gap: 'xl',
      })}
    >
      <div className="flex justify-between">
        <Tabs
          value={p.activeTab}
          onChange={(_, t) => p.dispatch('selectTab', t)}
        >
          <Tab value="Library" label={<Typography>Library</Typography>} />
          <Tab value="Upload" label={<Typography>Upload</Typography>} />
        </Tabs>
        <IconButton
          variant="text"
          color="neutral"
          icon={p.smallerView ? faMagnifyingGlassPlus : faMagnifyingGlassMinus}
          onClick={() => p.dispatch('toggleViewSize')}
        />
      </div>
      <TabContent {...p} />
      <ConfirmationModal
        isOpen={p.confirmationModal.open}
        onClose={() => p.dispatch('closeConfirmation')}
        onConfirm={() => p.dispatch('confirmCancel')}
      />
      <div
        className={clsx(
          'flex justify-between items-center px-6 py-4 border-t',
          sprinkles({ borderColor: 'N-40' }),
        )}
      >
        {p.footerButtons.map((b) => (
          <Button key={b.label} {...b.props}>
            {b.label}
          </Button>
        ))}
      </div>
    </div>
  );
};

const TabContent = (p: AddFromLibaryVM) => {
  switch (p.activeTab) {
    case 'Library':
      return (
        <Library
          {...p.library}
          videoType={p.videoType}
          smallerView={p.smallerView}
          multipleChoice={p.isMultipleChoice}
          enableFilteringByExtrasVideo={!p.isMultipleChoice} // todo: fix this when implementing proper filters
        />
      );
    case 'Upload':
      return <SingleUpload {...p} />;
  }
};
